import React, { useMemo, useState } from 'react';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SEARCE_COLORS } from '../../theme';
import SectionTitle from './SectionTitle';
import type { RaidItem, RaidStatus, RaidType } from './executiveSummaryTypes';

type RaidFilter = 'all' | RaidType;

const TAB_OPTIONS: { id: RaidFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'risk', label: 'Risks' },
  { id: 'issue', label: 'Issues' },
  { id: 'action', label: 'Actions' },
];

type Severity = 'low' | 'med' | 'high';

const SEVERITY_BG: Record<Severity, string> = {
  low: '#22c55e',
  med: '#eab308',
  high: '#ef4444',
};

/**
 * Cell colour is determined by p + i (anti-diagonal rule):
 *   p+i < 4  → green  (below the main anti-diagonal)
 *   p+i = 4  → yellow (on the main anti-diagonal: (1,3),(2,2),(3,1))
 *   p+i > 4  → red    (above the main anti-diagonal)
 */
const MATRIX: Record<string, Severity> = {
  '1-1': 'low',
  '2-1': 'low',
  '1-2': 'low',
  '3-1': 'med',
  '2-2': 'med',
  '1-3': 'med',
  '3-2': 'high',
  '2-3': 'high',
  '3-3': 'high',
};

const TYPE_PILL: Record<RaidType, { bg: string; color: string; label: string }> = {
  risk: { bg: '#fff7ed', color: '#c2410c', label: 'Risk' },
  issue: { bg: '#fef2f2', color: '#b91c1c', label: 'Issue' },
  action: { bg: '#eff6ff', color: SEARCE_COLORS.primary, label: 'Action' },
};

const STATUS_BADGE: Record<RaidStatus, { bg: string; color: string }> = {
  Open: { bg: '#fef2f2', color: '#c62828' },
  'In Progress': { bg: '#fffbeb', color: '#b45309' },
  Complete: { bg: '#ecfdf5', color: SEARCE_COLORS.success },
  Closed: { bg: '#f1f5f9', color: '#475569' },
};

interface RaidSectionProps {
  items: RaidItem[];
}

const RaidSection: React.FC<RaidSectionProps> = ({ items }) => {
  const [filter, setFilter] = useState<RaidFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((r) => r.type === filter)),
    [items, filter],
  );

  const selected = useMemo(
    () => (selectedId ? items.find((r) => r.id === selectedId) ?? null : null),
    [items, selectedId],
  );

  const handleFilterChange = (next: RaidFilter) => {
    setFilter(next);
    setSelectedId(null);
  };

  const handleSelect = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: `1px solid ${SEARCE_COLORS.border}`,
        borderRadius: '10px',
        p: '20px 24px',
        boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 0 0 1px rgba(0,0,0,.03)',
        mb: '20px',
      }}
    >
      <Box sx={{ display: 'block' }}>
        <SectionTitle>Risks &amp; Issues</SectionTitle>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: '32px',
          rowGap: '24px',
          alignItems: 'start',
        }}
      >
        {/* LEFT: tabs + matrix + detail */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', borderBottom: `2px solid ${SEARCE_COLORS.border}`, mb: '12px' }}>
            {TAB_OPTIONS.map((tab) => {
              const active = filter === tab.id;
              return (
                <Box
                  component="button"
                  type="button"
                  key={tab.id}
                  onClick={() => handleFilterChange(tab.id)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    borderBottom: '2px solid transparent',
                    mb: '-2px',
                    p: '8px 14px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    color: active ? SEARCE_COLORS.primary : SEARCE_COLORS.neutral_500,
                    borderBottomColor: active ? SEARCE_COLORS.primary : 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>

          <Typography
            sx={{
              m: '4px 0 6px',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: SEARCE_COLORS.neutral_500,
            }}
          >
            Probability × Impact
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', overflowX: 'auto', pb: '8px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '32px repeat(3, minmax(54px, 1fr)) 28px',
                gridTemplateRows: '20px repeat(3, 52px) 26px',
                gap: '5px',
                width: 'min(100%, 300px)',
              }}
            >
              {/* Probability axis title — top, spanning 3 data columns */}
              <Box sx={{ gridRow: 1, gridColumn: 1 }} />
              <Box
                sx={{
                  gridRow: 1,
                  gridColumn: '2 / 5',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  color: SEARCE_COLORS.neutral_500,
                }}
              >
                Probability
              </Box>
              <Box sx={{ gridRow: 1, gridColumn: 5 }} />

              {/* Impact axis title — left, spanning all 3 data rows */}
              <Box
                sx={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  gridRow: '2 / 5',
                  gridColumn: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  color: SEARCE_COLORS.neutral_500,
                }}
              >
                Impact
              </Box>

              {/* 9 matrix cells — columns 2-4, rows 2-4 */}
              {[1, 2, 3].map((iRow) =>
                [1, 2, 3].map((pCol) => {
                  const key = `${pCol}-${iRow}`;
                  const sev = MATRIX[key];
                  const cellItems = filtered.filter((r) => r.p === pCol && r.i === iRow);
                  const isHighlighted = selected && selected.p === pCol && selected.i === iRow;
                  const hasItems = cellItems.length > 0;
                  return (
                    <Box
                      key={key}
                      component={hasItems ? 'button' : 'div'}
                      type={hasItems ? 'button' : undefined}
                      onClick={hasItems ? () => handleSelect(cellItems[0].id) : undefined}
                      sx={{
                        gridRow: 5 - iRow,
                        gridColumn: pCol + 1,
                        background: SEVERITY_BG[sev],
                        borderRadius: '5px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4px',
                        p: '4px',
                        minHeight: 44,
                        position: 'relative',
                        outline: isHighlighted ? `2px solid ${SEARCE_COLORS.primary}` : 'none',
                        outlineOffset: isHighlighted ? '2px' : 0,
                        zIndex: isHighlighted ? 1 : 0,
                        color: sev === 'med' || sev === 'high' ? '#1c1917' : '#fff',
                        cursor: hasItems ? 'pointer' : 'default',
                        border: 'none',
                        fontFamily: 'inherit',
                        transition: 'filter 0.15s',
                        '&:hover': hasItems ? { filter: 'brightness(0.93)' } : {},
                      }}
                    >
                      {cellItems.map((item) => (
                        <Box
                          component="span"
                          key={item.id}
                          sx={{
                            minWidth: 26,
                            height: 26,
                            px: '4px',
                            borderRadius: '50%',
                            background: SEARCE_COLORS.primary,
                            color: '#fff',
                            border: '2px solid #fff',
                            fontWeight: 800,
                            fontSize: '0.625rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {item.id}
                        </Box>
                      ))}
                    </Box>
                  );
                }),
              )}

              {/* Impact row labels — right column (col 5), Hi at top (row 2), Low at bottom (row 4) */}
              {(['Low', 'Med', 'Hi'] as const).map((label, idx) => (
                <Box
                  key={`impact-row-${label}`}
                  sx={{
                    gridRow: 4 - idx,
                    gridColumn: 5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '0.5625rem',
                    fontWeight: 600,
                    color: SEARCE_COLORS.neutral_500,
                    lineHeight: 1,
                  }}
                >
                  {label}
                </Box>
              ))}

              {/* Probability axis labels — row 5, one per data column */}
              <Box sx={{ gridRow: 5, gridColumn: 1 }} />
              {(['Low', 'Med', 'Hi'] as const).map((label, idx) => (
                <Box
                  key={`prob-${label}`}
                  sx={{
                    gridRow: 5,
                    gridColumn: idx + 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    color: SEARCE_COLORS.neutral_500,
                  }}
                >
                  {label}
                </Box>
              ))}
              <Box sx={{ gridRow: 5, gridColumn: 5 }} />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 12px',
              mt: '6px',
              fontSize: '0.6875rem',
              color: SEARCE_COLORS.neutral_500,
            }}
          >
            {(
              [
                ['Low', SEVERITY_BG.low],
                ['Medium', SEVERITY_BG.med],
                ['High', SEVERITY_BG.high],
              ] as const
            ).map(([label, color]) => (
              <Box key={label} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '3px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    mr: '3px',
                    background: color,
                  }}
                />
                {label}
              </Box>
            ))}
          </Box>

          {selected && (
            <Box
              sx={{
                background: '#eef4fc',
                borderRadius: '10px',
                p: '16px 18px',
                mt: '12px',
                border: '1px solid #dbeafe',
                position: 'relative',
              }}
            >
              <IconButton
                size="small"
                onClick={() => setSelectedId(null)}
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: '#fff',
                  border: `1px solid ${SEARCE_COLORS.border}`,
                  color: SEARCE_COLORS.neutral_500,
                }}
                aria-label="Close detail"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: SEARCE_COLORS.primary,
                  mb: '12px',
                  pr: '36px',
                }}
              >
                {selected.title}
              </Typography>
              {[
                { label: 'Observation', value: selected.obs },
                { label: 'Action Plan', value: selected.act },
                { label: 'Owner', value: selected.owner },
                { label: 'Due Date', value: selected.due },
              ].map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '96px 1fr',
                    gap: '5px 12px',
                    mb: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: SEARCE_COLORS.neutral_500,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: SEARCE_COLORS.textSecondary, lineHeight: 1.5 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: '5px 12px' }}>
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: SEARCE_COLORS.neutral_500,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}
                >
                  Status
                </Typography>
                <Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: '10px',
                      py: '3px',
                      borderRadius: '999px',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      background: STATUS_BADGE[selected.status].bg,
                      color: STATUS_BADGE[selected.status].color,
                    }}
                  >
                    {selected.status}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* RIGHT: list of cards */}
        <Box
          sx={{
            pl: { xs: 0, md: '20px' },
            borderLeft: { xs: 'none', md: `2px solid ${SEARCE_COLORS.border}` },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            sx={{
              mb: '10px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: SEARCE_COLORS.textPrimary,
            }}
          >
            Open risks, issues &amp; actions
          </Typography>

          {filtered.length === 0 ? (
            <Box
              sx={{
                p: '12px',
                fontSize: '0.8125rem',
                color: SEARCE_COLORS.neutral_500,
                background: '#fafafa',
                borderRadius: '10px',
                border: `1px dashed ${SEARCE_COLORS.border}`,
              }}
            >
              No items match this filter.
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maxHeight: 'min(400px, 55vh)',
                overflowY: 'auto',
                pr: '4px',
              }}
            >
              {filtered.map((item) => {
                const typePill = TYPE_PILL[item.type];
                const isSel = selected?.id === item.id;
                return (
                  <Box
                    component="button"
                    type="button"
                    key={item.id}
                    onClick={() => handleSelect(item.id)}
                    sx={{
                      width: '100%',
                      p: '12px 14px',
                      textAlign: 'left',
                      font: 'inherit',
                      color: 'inherit',
                      background: isSel ? '#eef4fc' : '#f8fafc',
                      border: `1px solid ${isSel ? SEARCE_COLORS.primary : '#e2e8f0'}`,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      boxShadow: isSel ? `0 0 0 2px rgba(0, 100, 255, 0.15)` : 'none',
                      transition: 'border-color .15s, box-shadow .15s',
                      '&:hover': { borderColor: '#94a3b8', background: '#f1f5f9' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '6px 8px',
                        mb: '6px',
                        fontSize: '0.75rem',
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 800, color: SEARCE_COLORS.primary }}>
                        {item.id}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.625rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          px: '7px',
                          py: '2px',
                          borderRadius: '999px',
                          background: typePill.bg,
                          color: typePill.color,
                        }}
                      >
                        {typePill.label}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          ml: 'auto',
                          color: SEARCE_COLORS.neutral_500,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Due {item.due}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        lineHeight: 1.45,
                      }}
                    >
                      {item.title}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default RaidSection;
